<script setup lang="ts">
import { computed, type PropType, ref } from 'vue';
import Card from 'primevue/card';
import Button from 'primevue/button';
import Menu, { type MenuPassThroughMethodOptions } from 'primevue/menu';
import { type FormField, DateMode, FIELD_TYPES, FormFieldType } from '@/models';
import { ButtonType, CustomButton, RichInput, Spacer, FormFieldConfig, FormFieldBottomButtons,
  FieldDeleteAlertDialog, ChoiceFieldRecodeValuesDialog, DropdownFieldChoiceDialog, MatrixFieldConfigDialog,
  FieldSettingsDialog, EffectLabels
} from '@/components';
import { useFormBuilder } from '@/stores/formBuilder';

const props = defineProps({
  field: {
    type: Object as PropType<FormField>,
    required: true
  }
});

const formBuilderStore = useFormBuilder();

const localField = ref<FormField>(props.field);

const types = ref();
const currentSelectedType = computed(() =>
  FIELD_TYPES.find(type => type.type === localField.value.type)
);

const handleTypeClick = (event: PointerEvent) => {
  types.value.toggle(event);
};

const handleFieldExpansion = () => {
  localField.value.expanded = !localField.value.expanded;
};

const emits = defineEmits([
  'onAddField',
  'onDeleteField',
  'onCloneField',
  'onReorderFields',
  'onFieldTypeChange',
  'onChildFieldClick'
]);

const onAddField = () => {
  emits('onAddField', localField.value);
};

const onDeleteField = () => {
  emits('onDeleteField', localField.value);
};

const onCloneField = () => {
  emits('onCloneField', localField.value);
};

const onReorderFields = () => {
  emits('onReorderFields');
};

const onFieldTypeChange = () => {
  emits('onFieldTypeChange', localField.value);
};

const onChildFieldClick = (field: FormField) => {
  emits('onChildFieldClick', field);
};

const openFieldDeleteAlert = ref(false);

const handleDeleteAlert = () => {
  openFieldDeleteAlert.value = !openFieldDeleteAlert.value;
};

const openChoiceRecode = ref(false);
const handleChoiceRecode = () => {
  openChoiceRecode.value = !openChoiceRecode.value;
};

const openDropdownChoices = ref(false);
const handleDropdownChoices = () => {
  openDropdownChoices.value = !openDropdownChoices.value;
};

const openMatrixConfig = ref(false);
const handleMatrixConfigDialog = () => {
  openMatrixConfig.value = !openMatrixConfig.value;
};


const openFieldSettingsDialog = ref(false);
const handleFieldSettingDialog = () => {
  openFieldSettingsDialog.value = !openFieldSettingsDialog.value;
};

const handleFieldTypeChange = (optionsIndex: number) => {
  const type = FIELD_TYPES[optionsIndex].type;
  localField.value.type = type;

  onFieldTypeChange();
  const addChoice = () => {
    localField.value.options.maximumChoicesCount = 1;
    localField.value.options.choices = [
      {
        id: 1,
        label: 'Example choice',
        value: 1
      }
    ];
  };

  switch (type) {
    case FormFieldType.CHOICE:
      if (!localField.value.options.choices) {
        addChoice();
      }
      break;
    
    case FormFieldType.DROPDOWN:
      if (!localField.value.options.choices) {
        addChoice();
      }
      break;

    case FormFieldType.DATE:
      localField.value.options.mode = DateMode.DATE;
      break;

    case FormFieldType.FILE:
      localField.value.options.maximumSize = {
        enabled: false,
        value: null
      };
      localField.value.options.maximumFileNumber = {
        enabled: false,
        value: null
      };
      localField.value.options.allowedExtensions = {
        enabled: false,
        value: null
      };
      break;

    case FormFieldType.MATRIX:
      if (!localField.value.options.rows) {
        localField.value.options.rows = [];
        localField.value.options.columns = [];
      }
      break;
  
    default:
      break;
  }

  localField.value.type = type;
};
</script>

<template>
  <Card :class="['form-field-card', { 'form-field-error': localField.error, 'section-child-field': localField.sectionId }]">
    <template #content>
      <div class="alert error" v-if="localField.error">
        <FontAwesomeIcon :icon="['far', 'circle-exclamation']" class="icon" />
        <div class="label">{{ localField.error }}</div>
      </div>
      
      <section class="field-config-expanded">
        <section class="field-type-settings" v-if="field.expanded === true">
          <div class="field-type-group">
            <Button icon="pi pi-sort-down-fill"
              outlined
              class="arrow-button"
              @click="handleFieldExpansion()"
              v-ripple/>
            <CustomButton :text="currentSelectedType?.type"
              :icon="{ style: currentSelectedType!.iconStyle, icon: currentSelectedType!.icon }"
              :font-weight="700"
              @onClick="handleTypeClick"
              class="field-type-button"
              :type="ButtonType.WITHOUT_OUTLINE"/>
            <Menu :model="FIELD_TYPES"
              :popup="true"
              ref="types"
              :pt="{
                itemLink: (options: MenuPassThroughMethodOptions) =>({
                  class: {
                    'selected-menu': options.context.index === FIELD_TYPES.findIndex(type => type.type === localField.type)
                  },
                  onClick: () => handleFieldTypeChange(options.context.index)
                })
              }">
              <template #item="{ item, props }">
                <a v-ripple class="flex items-center" v-bind="props.action">
                  <FontAwesomeIcon :icon="[`${item.iconStyle}`, `${item.icon}`]"
                    class="icon"/>
                  <div class="type-label">{{ item.label }}</div>
                </a>
              </template>
            </Menu>
          </div>
          <div class="field-edit-settings" v-if="field.expanded === true">
            <CustomButton text="Edit settings"
              :icon="{ style: 'fal', icon: 'gear' }"
              class="edit-settings-button"
              @onClick="handleFieldSettingDialog"
              :type="ButtonType.WITHOUT_OUTLINE"/>

            <EffectLabels :field="localField"/>
          </div>
        </section>
        <section class="field-details">
          <RichInput v-model="localField.label"
            :suggestions="formBuilderStore.form!.items"
            v-if="field.expanded === true"
            placeholder="Enter field label, type @ to select other field labels..."/>
          <Spacer height="4" width="100%" v-if="field.expanded" />
          <RichInput v-model="localField.description"
            :suggestions="formBuilderStore.form!.items"
            placeholder="Enter field description, type @ to select other field labels..."
            v-if="field.expanded === true"/>
          <Spacer height="16" width="100%" v-if="field.expanded === true" />
          <FormFieldConfig :formField="localField"
            v-if="field.expanded === true"
            @onRecodeValue="handleChoiceRecode"
            @onEditDropdownChoices="handleDropdownChoices"
            @onEditMatrixTable="handleMatrixConfigDialog"
            @onChildFieldClick="onChildFieldClick" />
          <Spacer height="60" width="100%" v-if="field.expanded === true" />
        </section>
      </section>
      <section class="field-config-collapsed">
        <div class="field-type-group" v-if="field.expanded === false">
          <Button icon="pi pi-sort-down-fill"
            outlined
            class="arrow-button-collapsed"
            @click="handleFieldExpansion()"
            v-ripple/>
          <CustomButton :text="currentSelectedType?.type"
            :icon="{ style: currentSelectedType!.iconStyle, icon: currentSelectedType!.icon }"
            :font-weight="700"
            @onClick="handleTypeClick"
            class="field-type-button"
            :type="ButtonType.WITHOUT_OUTLINE"/>
          <Menu :model="FIELD_TYPES"
            :popup="true"
            ref="types"
            :pt="{
                itemLink: (options: MenuPassThroughMethodOptions) =>({
                  class: {
                    'selected-menu': options.context.index === FIELD_TYPES.findIndex(type => type.type === localField.type)
                  },
                  onClick: () => handleFieldTypeChange(options.context.index)
                })
              }">
            <template #item="{ item, props }">
              <a v-ripple class="flex items-center" v-bind="props.action">
                <FontAwesomeIcon :icon="[`${item.iconStyle}`, `${item.icon}`]"
                  class="icon"/>
                <div class="type-label">{{ item.label }}</div>
              </a>
            </template>
          </Menu>
        </div>
        <div class="label-buttons">
          <div class="collapsed-field-label">
            <RichInput v-model="localField.label"
              :suggestions="formBuilderStore.form!.items"
              v-if="field.expanded === false"
              placeholder="Enter field label, type @ to select other field labels..."/>
          </div>
          <FormFieldBottomButtons :field="localField"
            @onAddField="onAddField"
            @onDeleteField="handleDeleteAlert"
            @onCloneField="onCloneField"
            @onReorderFields="onReorderFields"/>
        </div>
      </section>
    </template>
  </Card>

  <FieldDeleteAlertDialog :visible="openFieldDeleteAlert"
    :fieldLabel="localField.label"
    @onClose="handleDeleteAlert"
    @onSave="onDeleteField"/>

  <ChoiceFieldRecodeValuesDialog :visible="openChoiceRecode"
    :field="localField"
    v-if="openChoiceRecode"
    @onClose="handleChoiceRecode"/>

  <DropdownFieldChoiceDialog :visible="openDropdownChoices"
    v-if="openDropdownChoices"
    :field="localField"
    @onClose="handleDropdownChoices"/>

  <MatrixFieldConfigDialog :visible="openMatrixConfig"
    v-if="openMatrixConfig"
    :field="localField"
    @onClose="handleMatrixConfigDialog"/>

  <FieldSettingsDialog :visible="openFieldSettingsDialog"
    :formField="localField"
    v-if="openFieldSettingsDialog"
    @onClose="handleFieldSettingDialog"/>
</template>

<style scoped lang="scss">
@import "../../../assets/main.scss";
.p-card {
  border-radius: 4px;
  box-shadow: 0 0 0 1px #ebeff2, 0 1px 10px 1px rgba(15, 15, 15, 0.05),
    0 1px 3px 1px rgba(15, 15, 15, 0.05);
  padding: 20px 28px;

  :deep(.p-card-body) {
    padding: 0px;
  }
}
.form-field-card {
  max-width: 1000px;
  margin-left: 0px;
}
.form-field-error {
  background-color: #ffe5e8;
}
.field-config-expanded {
  display: flex;
}
.field-type-settings {
  min-width: 200px;

  .field-edit-settings {
    margin-top: 4px;
  }
}

.section-child-field {
  margin-left: 40px;
}

.field-type-group {
  min-width: 200px;
  display: flex;
  align-items: center;

  :deep(.p-button-outlined) {
    border: none;
    border-radius: 4px;
    color: black;

    &:not(:disabled):hover {
      border: none;
      background-color: $disabled;
      color: black;
    }
  }

  .field-type-button {
    margin-left: 12px;
    font-size: 1rem;
  }
}

.arrow-button-collapsed {
  transform: rotate(-90deg);
}
.field-config-collapsed {
  display: flex;
}
.label-buttons {
  width: 100%;
  display: flex;

  .collapsed-field-label {
    flex: 1;
  }
}
.p-menu-item-link {
  .icon {
    font-size: 16px;
  }
  .type-label {
    font-weight: 500;
    font-size: 16px;
  }
}
.selected-menu {
  font-weight: 700;
  background-color: #e2e8f0;
  color: black;
  border-radius: 4px;
}
.field-details {
  width: 100%;
  min-width: 0;
}
.input-title {
  width: 100%;
}
.alert {
  padding: 12px;
  border-radius: 4px;
  display: flex;
  align-items: center;
  font-size: 16px;

  .icon {
    font-size: 20px;
    margin-right: 8px;
  }
}
.error {
  color: #5c0a11;
  border: 1px solid #ea3546;
  background-color: #fef5f6;
  margin-top: 8px;

  .icon {
    color: #ea3546;
  }
}
</style>
